/**
 * Sync buffer for spreadsheet / backend reconciliation.
 * Tracks which cells were modified by spreadsheet-priority vs backend-priority operations
 * so we can merge and sync up/down in one debounced tick.
 *
 * Spreadsheet priority (source of truth: spreadsheet): cell CRUD, column create/delete,
 * undo/redo, copy/paste, sorting, import (CSV/CRM).
 * DB priority (source of truth: database/backend): enrichment, prioritization, target persona,
 * scheduling, column schema, AI GTM engineer.
 */

export const SYNC_BUFFER_CELL_KEY = (
  rowId: number,
  columnName: string
): string => `${rowId},${columnName}`;

export interface SyncBufferState {
  /** Cell keys (rowId,colName) modified by spreadsheet-priority since last sync. */
  spreadsheetDirtyKeys: Set<string>;
  /** Cell keys modified by DB-priority since last sync (e.g. enrichment). */
  dbPriorityKeys: Set<string>;
}

export interface SyncBufferRef {
  current: SyncBufferState;
}

export function createSyncBuffer(): SyncBufferState {
  return {
    spreadsheetDirtyKeys: new Set(),
    dbPriorityKeys: new Set()
  };
}

export function addSpreadsheetDirtyKey(
  buffer: SyncBufferState,
  key: string
): void {
  buffer.spreadsheetDirtyKeys.add(key);
}

export function addSpreadsheetDirtyRange(
  buffer: SyncBufferState,
  columnNamesByIndex: string[],
  range: {
    startRow: number;
    endRow: number;
    startColumn: number;
    endColumn: number;
  }
): void {
  for (let r = range.startRow; r <= range.endRow; r++) {
    for (let c = range.startColumn; c <= range.endColumn; c++) {
      const colName = columnNamesByIndex[c];
      if (colName != null)
        addSpreadsheetDirtyKey(buffer, SYNC_BUFFER_CELL_KEY(r, colName));
    }
  }
}

/** Mark entire sheet as spreadsheet-dirty (e.g. sort, paste, undo/redo that affect many cells). */
export function addSpreadsheetDirtyFullSheet(
  buffer: SyncBufferState,
  columnNamesByIndex: string[],
  rowCount: number
): void {
  for (let r = 0; r < rowCount; r++) {
    for (const colName of columnNamesByIndex) {
      if (colName != null)
        addSpreadsheetDirtyKey(buffer, SYNC_BUFFER_CELL_KEY(r, colName));
    }
  }
}

export function addDbPriorityKey(buffer: SyncBufferState, key: string): void {
  buffer.dbPriorityKeys.add(key);
}

export function addDbPriorityCell(
  buffer: SyncBufferState,
  rowId: number,
  columnName: string
): void {
  addDbPriorityKey(buffer, SYNC_BUFFER_CELL_KEY(rowId, columnName));
}

export function clearSyncBuffer(buffer: SyncBufferState): void {
  buffer.spreadsheetDirtyKeys.clear();
  buffer.dbPriorityKeys.clear();
}

/** Snapshot for use during sync; clears the buffer after copying. */
export function takeSyncBufferSnapshot(buffer: SyncBufferState): {
  spreadsheetDirtyKeys: Set<string>;
  dbPriorityKeys: Set<string>;
} {
  const spreadsheetDirtyKeys = new Set(buffer.spreadsheetDirtyKeys);
  const dbPriorityKeys = new Set(buffer.dbPriorityKeys);
  clearSyncBuffer(buffer);
  return { spreadsheetDirtyKeys, dbPriorityKeys };
}
